import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Briefcase, Target, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useChannels, useDashboard } from '@/features/command-center';
import { useObjectives } from '../okr/queries';

const SOURCE_LABEL: Record<string, string> = {
  upwork: 'Upwork',
  linkedin: 'LinkedIn',
  website: 'Website',
  referral: 'Referral',
  outbound: 'Outbound',
  other: 'Other',
};

/** Cross-module analytics — composes the existing Command Center endpoints
 *  with OKR overall progress. Read-only. */
export function AnalyticsDashboardPage() {
  const dashboard = useDashboard();
  const channels = useChannels();
  const objectives = useObjectives();

  const data = dashboard.data;
  const channelRows = channels.data ?? [];

  // OKR roll-up: avg overall progress (mean of mean-of-KRs per objective).
  const objs = objectives.data?.results ?? [];
  const okrProgress = objs.length
    ? Math.round(
        objs.reduce((sum, o) => {
          if (!o.key_results.length) return sum;
          const avg =
            o.key_results.reduce((s, kr) => s + kr.progress_percent, 0) / o.key_results.length;
          return sum + avg;
        }, 0) / objs.length,
      )
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground text-sm">
            Cross-module read-only dashboard. Source data lives in CRM, Projects, Finance, OKR.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/analytics/kpis">
              <Target className="size-4" /> KPIs &amp; Goals
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/analytics/exports">
              Exports <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      {dashboard.isError ? (
        <p className="text-destructive text-sm">
          {(dashboard.error as unknown as ApiError)?.message ?? 'Failed to load dashboard.'}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader>
              <CardDescription>Open deals</CardDescription>
              <CardTitle className="text-3xl tabular-nums">
                {dashboard.isPending ? <Skeleton className="h-9 w-16" /> : data?.pipeline?.open_deals ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>Active projects</CardDescription>
              <CardTitle className="text-3xl tabular-nums">
                {dashboard.isPending ? <Skeleton className="h-9 w-16" /> : data?.projects.active ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>Tracked hours</CardDescription>
              <CardTitle className="text-3xl tabular-nums">
                {dashboard.isPending ? (
                  <Skeleton className="h-9 w-16" />
                ) : (
                  Number(data?.projects.tracked_hours ?? 0).toFixed(2)
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>OKR progress</CardDescription>
              <CardTitle className="text-3xl tabular-nums">
                {objectives.isPending ? (
                  <Skeleton className="h-9 w-16" />
                ) : okrProgress != null ? (
                  `${okrProgress}%`
                ) : (
                  '—'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground text-xs">
              Across {objs.length} active objective{objs.length === 1 ? '' : 's'}.
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0">
            <Users className="text-muted-foreground size-5" />
            <CardTitle className="text-base">Lead inflow by channel</CardTitle>
          </CardHeader>
          <CardContent>
            {channels.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : channelRows.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {channelRows.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell>{SOURCE_LABEL[row.source] ?? row.source}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.leads}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-sm">No leads recorded yet.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0">
            <Briefcase className="text-muted-foreground size-5" />
            <CardTitle className="text-base">Recent objectives</CardTitle>
          </CardHeader>
          <CardContent>
            {objectives.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : objs.length ? (
              <ul className="space-y-2">
                {objs.slice(0, 4).map((o) => {
                  const avg = o.key_results.length
                    ? Math.round(
                        o.key_results.reduce((s, kr) => s + kr.progress_percent, 0) /
                          o.key_results.length,
                      )
                    : 0;
                  return (
                    <li key={o.id} className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm">{o.title}</span>
                      <Badge variant="outline" className="tabular-nums">
                        {avg}%
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">No objectives yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0">
          <BarChart3 className="text-muted-foreground size-5" />
          <CardTitle className="text-base">Where to drill in</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-2 text-sm sm:grid-cols-2">
          <Link to="/command-center" className="hover:text-foreground">
            → Command Center (executive view)
          </Link>
          <Link to="/finance/reports" className="hover:text-foreground">
            → Finance Reports (P&amp;L, revenue by client)
          </Link>
          <Link to="/projects/reports" className="hover:text-foreground">
            → Project Reports (time, billable %)
          </Link>
          <Link to="/finance/budgeting" className="hover:text-foreground">
            → Budgeting (project budget vs actual)
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
